import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeScannerState } from "html5-qrcode";
const QRScanner = ({ elementId, onScan, openManual }) => {
  const [cameraId, setCameraId] = useState(null);
  // const [availableCameras, setAvailableCameras] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [zoomSupported, setZoomSupported] = useState(false);
  const [zoomValue, setZoomValue] = useState(1);
  const [zoomRange, setZoomRange] = useState({ min: 1, max: 1, step: 0.1 });
  const [error, setError] = useState(null);
  const [focus, setFocus] = useState("none");
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [capabilitiestest, setcapabilitiestest] = useState(null);
  const html5QrCodeRef = useRef(null);
  const mountedRef = useRef(false);
  const hasScannedRef = useRef(false);
  const videoTrackRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    if (!openManual) {
      setTimeout(() => startScanner(), 150);
    }
    return () => {
      mountedRef.current = false;
      stopScanner();
    };
  }, [openManual]);

  // const initializeScanner = async () => {
  //   try {
  //     const container = document.getElementById(elementId);
  //     if (!container) {
  //       setError(`Element with ID "${elementId}" not found in DOM.`);
  //       return;
  //     }

  //     // const devices = await Html5Qrcode.getCameras();
  //     // setAvailableCameras(devices);
  //     // if (!devices.length) {
  //     //   setError("No cameras found.");
  //     //   return;
  //     // }

  //     // const backCam = devices.find((d) => /back|environment/i.test(d.label));
  //     // const selected = backCam || devices[0];
  //     // setCameraId(selected.id);

  //     // if (!openManual) {
  //       await startScanner();
  //     // }
  //   } catch (err) {
  //     setError("Failed to initialize scanner: " + (err?.message || err));
  //   }
  // };
  const startScanner = async () => {
    try {
      const element = document.getElementById(elementId);
      if (!element || !document.body.contains(element)) {
        throw new Error(`Element with ID "${elementId}" not found in DOM.`);
      }

      if (html5QrCodeRef.current) {
        await stopScanner();
      }

      const html5QrCode = new Html5Qrcode(elementId);
      html5QrCodeRef.current = html5QrCode;
      hasScannedRef.current = false;
      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 30,
          qrbox: 250,
          experimentalFeatures: {
            useBarCodeDetectorIfSupported: true,
          },
        },
        async (decodedText) => {
          if (hasScannedRef.current) return; // Ignore if already scanned
          hasScannedRef.current = true;
          let data;
          try {
            // Attempt JSON parse first
            data = JSON.parse(decodedText);
          } catch (err) {
            // console.warn("Not JSON, falling back to plain text parsing.");
            data = {};

            // Split plain text by newline
            decodedText.split("\n").forEach((line) => {
              const [key, value] = line.split(":");
              if (key && value) {
                const normalizedKey = key
                  .trim()
                  .toLowerCase()
                  .replace(/[^a-z0-9]/gi, "");
                data[normalizedKey] = value.trim();
              }
            });
          }
          const normalizedData = {};
          Object.keys(data).forEach((key) => {
            const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/gi, "");
            normalizedData[normalizedKey] = data[key];
          });
          if (onScan) onScan(normalizedData);
          await stopScanner();
        }
      );

      setIsScanning(true);

      // ✅ At this point, the video element is guaranteed to exist and be streaming
      const videoElement = document.querySelector(`#${elementId} video`);
      if (!videoElement) {
        console.warn("Video element not found after starting scanner.");
        return;
      }
      const track = videoElement.srcObject?.getVideoTracks?.()[0];
      if (!track) {
        console.warn("No video track found.");
        return;
      }
      videoTrackRef.current = track;
      const capabilities = track.getCapabilities?.();
      setcapabilitiestest(JSON.stringify(capabilities, null, 2));
      if (capabilities?.focusMode?.includes("continuous")) {
        await track.applyConstraints({
          advanced: [{ focusMode: "continuous" }],
        });
        setFocus("continuous");
      } else if (capabilities?.focusMode?.includes("manual")) {
        setFocus("manual");
      } else {
        setFocus("none");
      }
      if (capabilities?.zoom) {
        setZoomSupported(true);
        setZoomRange({
          min: capabilities.zoom.min,
          max: capabilities.zoom.max,
          step: capabilities.zoom.step || 0.1,
        });
        setZoomValue(capabilities.zoom.min);
      }

      // ✅ Torch
      if (capabilities?.torch) {
        setTorchSupported(true);
      }
    } catch (err) {
      console.error("Failed to start scanner:", err);
      setError("Failed to start scanner: " + err.message);
    }
  };

  const stopScanner = async () => {
    setZoomSupported(false);
    setZoomRange({});

    try {
      const scanner = html5QrCodeRef.current;
      if (!scanner) return;
      const state = scanner.getState?.();
      if (
        state === Html5QrcodeScannerState.SCANNING ||
        state === Html5QrcodeScannerState.PAUSED
      ) {
        await scanner.stop();
      }
      if (videoTrackRef.current) {
        videoTrackRef.current.stop();
        videoTrackRef.current = null;
      }
      html5QrCodeRef.current = null;
      setIsScanning(false);
    } catch (err) {
      console.error("Failed to stop scanner:", err);
    }
  };

  const handleZoomChange = async (value) => {
    setZoomValue(value);
    try {
      const track = videoTrackRef.current;
      if (track?.readyState === "live") {
        await track.applyConstraints({
          advanced: [{ zoom: parseFloat(value) }],
        });
      }
    } catch (err) {
      console.warn("Zoom error:", err);
    }
  };

  const toggleTorch = async () => {
    const track = videoTrackRef.current;
    if (!track || track.readyState !== "live") return;

    try {
      await track.applyConstraints({
        advanced: [{ torch: !torchOn }],
      });
      setTorchOn((prev) => !prev);
    } catch (err) {
      console.warn("Torch toggle failed:", err);
    }
  };

  if (openManual) return null;

  return (
    <div>
      {/* {availableCameras.length > 1 && (
        <div style={{ marginBottom: '10px' }}>
          <select
            value={cameraId}
            onChange={async (e) => {
              const id = e.target.value;
              setCameraId(id);
              await stopScanner();
              setTimeout(() => startScanner(id), 200);
            }}
          >
            {availableCameras.map((cam) => (
              <option key={cam.id} value={cam.id}>
                {cam.label || cam.id}
              </option>
            ))}
          </select>
        </div>
      )} */}

      <div style={{ marginBottom: "10px" }}>
        {!isScanning ? (
          <button onClick={() => startScanner()}>Start Scanner</button>
        ) : (
          <button onClick={stopScanner}>Stop Scanner</button>
        )}
      </div>

      <div style={{ marginTop: 10 }}>
        <p>Focus Mode: {focus}</p>
      </div>

      <div
        id={elementId}
        // ref={videoElementRef}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "500px",
          height: "400px",
          border: "1px solid #ccc",
          marginTop: "10px",
          overflow: "hidden",
          backgroundColor: "#000",
        }}
      >
        {torchSupported && isScanning && (
          <button
            onClick={toggleTorch}
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              padding: "6px 10px",
              backgroundColor: torchOn ? "#ffc107" : "#333",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
              fontSize: "14px",
              zIndex: 10,
            }}
          >
            {torchOn ? "Flash Off" : "Flash On"}
          </button>
        )}

        {zoomSupported && (
          <div
            style={{
              position: "absolute",
              bottom: "10px",
              left: "10px",
              background: "rgba(0, 0, 0, 0.6)",
              padding: "6px 10px",
              borderRadius: "8px",
              color: "#fff",
              zIndex: 10,
              display: "flex",
              alignItems: "center",
            }}
          >
            <label style={{ fontSize: "12px" }}>Zoom</label>
            <input
              type="range"
              min={zoomRange.min}
              max={zoomRange.max}
              step={zoomRange.step}
              value={zoomValue}
              onChange={(e) => handleZoomChange(e.target.value)}
              style={{ width: "140px", marginLeft: "10px" }}
            />
            <span style={{ marginLeft: "8px" }}>
              {parseFloat(zoomValue).toFixed(1)}x
            </span>
          </div>
        )}
      </div>

      {error && <p style={{ color: "red" }}>{error}</p>}
    </div>
  );
};

export default QRScanner;
// const QRScanner = ({ elementId, onScan, openManual }) => {
//   const [isScanning, setIsScanning] = useState(false);
//   const [zoomSupported, setZoomSupported] = useState(false);
//   const [zoomValue, setZoomValue] = useState(1);
//   const [zoomRange, setZoomRange] = useState({ min: 1, max: 1, step: 0.1 });
//   const [error, setError] = useState(null);
//   const [focus, setFocus] = useState(false);
//   const [capabilitiestest, setcapabilitiestest] = useState(null);
//   const [torchSupported, setTorchSupported] = useState(false);
//   const [torchOn, setTorchOn] = useState(false);
//   const html5QrCodeRef = useRef(null);
//   const mountedRef = useRef(false);
//   const videoTrackRef = useRef(null); // ✅
//   useEffect(() => {
//     mountedRef.current = true;
//     if (!openManual) {
//       setTimeout(() => startScanner(), 150);
//     }
//     return () => {
//       mountedRef.current = false;
//       stopScanner();
//     };
//   }, []);

//   const startScanner = async () => {
//     try {
//       const element = document.getElementById(elementId);
//       if (!element || !document.body.contains(element)) {
//         throw new Error(`Element with ID "${elementId}" not found in DOM.`);
//       }

//       if (html5QrCodeRef.current) {
//         await stopScanner();
//       }

//       const html5QrCode = new Html5Qrcode(elementId);
//       html5QrCodeRef.current = html5QrCode;

//       // Start scanner temporarily with 30fps
//       await html5QrCode.start(
//         { facingMode: "environment" },
//         {
//           fps: 30, // Temporary; will be updated below if needed
//           qrbox: 250,
//           experimentalFeatures: {
//             useBarCodeDetectorIfSupported: true,
//           },
//         },
//         async (decodedText) => {
//           if (onScan) onScan(decodedText);
//           await stopScanner();
//         }
//       );
//       const videoElement = document.querySelector(`#${elementId}video`);
//       if (!videoElement) {
//         console.warn("video element not found after starting scanner");
//         return;
//       }
//       const track = videoElement.srcObject?.getVideoTracks?.()[0];
//       if (!track) {
//         console.warn("No video track  found");
//         return;
//       }
//       videoTrackRef.current = track;
//       const capabilities = track.getCapabilities?.();
//       setcapabilitiestest(JSON.stringify(capabilities, null, 2));

//       // ✅ Autofocus
//       if (capabilities?.focusMode?.includes("continuous")) {
//         await track.applyConstraints({
//           advanced: [{ focusMode: "continuous" }],
//         });
//         setFocus("continuous");
//       } else if (capabilities?.focusMode?.includes("manual")) {
//         setFocus("manual");
//       } else {
//         setFocus("none");
//       }

//       // 🔍 Zoom
//       if (capabilities?.zoom) {
//         setZoomSupported(true);
//         setZoomRange({
//           min: capabilities.zoom.min,
//           max: capabilities.zoom.max,
//           step: capabilities.zoom.step || 0.1,
//         });
//         setZoomValue(capabilities.zoom.min);

//         if (capabilities?.torch) {
//           setTorchSupported(true);
//           // imageCaptureRef.current = new window.ImageCapture(track);
//         }
//       }
//     } catch (err) {
//       console.error("Failed to start scanner:", err);
//       setError("Failed to start scanner: " + err?.message);
//     }
//   };

//   const stopScanner = async () => {
//     setZoomSupported(false);
//     setZoomRange({});
//     try {
//       const scanner = html5QrCodeRef.current;
//       if (!scanner) return;

//       const state = scanner.getState?.();
//       if (
//         state === Html5QrcodeScannerState.SCANNING ||
//         state === Html5QrcodeScannerState.PAUSED
//       ) {
//         await scanner.stop();
//       }
//       if (videoTrackRef.current) {
//         videoTrackRef.current.stop();
//         videoTrackRef.current = null;
//       }

//       html5QrCodeRef.current = null;
//       setIsScanning(false);
//     } catch (err) {
//       // console.error("Failed to stop scanner:", err);
//     }
//   };

//   const handleZoomChange = async (value) => {
//     setZoomValue(value);
//     try {
//       const track = videoTrackRef.current;
//       if (track?.readystate==='live') {
//         await track.applyConstraints({
//           advanced: [{ zoom: parseFloat(value) }],
//         });
//       }
//     } catch (err) {
//       console.error("Zoom error:", err);
//     }
//   };

//   const toggleTorch = async () => {
//     const track = videoTrackRef.current;
//     if (!track || track.readystate !=='live') return;

//     try {
//       await track.applyConstraints({
//         advanced: [{ torch: !torchOn }],
//       });
//       setTorchOn((prev) => !prev);
//     } catch (err) {
//       console.warn("Torch toggle failed:", err);
//     }
//   };

//   if (openManual) return null;

//   return (
//     <div>
//       {/* Start / Stop Buttons */}
//       <div style={{ marginBottom: "10px" }}>
//         {!isScanning ? (
//           <button onClick={() => startScanner()}>Start Scanner</button>
//         ) : (
//           <button onClick={stopScanner}>Stop Scanner</button>
//         )}
//       </div>

//       <div style={{ marginTop: 10 }}>
//         <p>Focus Mode: {focus}</p>
//       </div>

//       <div
//         id={elementId}
//         style={{
//           position: "relative",
//           width: "100%",
//           maxWidth: "500px",
//           height: "400px",
//           border: "1px solid #ccc",
//           marginTop: "10px",
//           overflow: "hidden",
//           backgroundColor: "#000",
//         }}
//       >
//         {/* 🔦 Torch Button - Top Right */}
//         {torchSupported && isScanning && (
//           <button
//             onClick={toggleTorch}
//             style={{
//               position: "absolute",
//               top: "10px",
//               right: "10px",
//               padding: "6px 10px",
//               backgroundColor: torchOn ? "#ffc107" : "#333",
//               color: "#fff",
//               border: "none",
//               borderRadius: "5px",
//               cursor: "pointer",
//               fontSize: "14px",
//               zIndex: 10,
//             }}
//           >
//             {torchOn ? "Flash Off" : "Flash On"}
//           </button>
//         )}

//         {/* 🔍 Zoom Slider - Bottom Left */}
//         {zoomSupported && (
//           <div
//             style={{
//               position: "absolute",
//               bottom: "10px",
//               left: "10px",
//               background: "rgba(0, 0, 0, 0.6)",
//               padding: "6px 10px",
//               borderRadius: "8px",
//               width: "100%",
//               color: "#fff",
//               zIndex: 10,
//             }}
//           >
//             <label style={{ fontSize: "12px" }}>Zoom</label>
//             <input
//               type="range"
//               min={zoomRange.min}
//               max={zoomRange.max}
//               step={zoomRange.step}
//               value={zoomValue}
//               onChange={(e) => handleZoomChange(e.target.value)}
//               style={{
//                 width: "140px",
//                 marginLeft: "10px",
//               }}
//             />
//             <span style={{ marginLeft: "8px" }}>{parseFloat(zoomValue).toFixed(1)}x</span>
//           </div>
//         )}
//       </div>
//       {error && <p style={{ color: "red" }}>{error}</p>}
//     </div>
//   );
// };

// export default QRScanner;

// const QRScanner = ({ elementId, onScan, openManual }) => {
//   const [cameraId, setCameraId] = useState(null);
//   const [availableCameras, setAvailableCameras] = useState([]);
//   const [isScanning, setIsScanning] = useState(false);
//   const [zoomSupported, setZoomSupported] = useState(false);
//   const [zoomValue, setZoomValue] = useState(1);
//   const [zoomRange, setZoomRange] = useState({ min: 1, max: 1, step: 0.1 });
//   const [error, setError] = useState(null);
//   const [focus, setFocus] = useState(false);
//   const [capabilitiestest, setcapabilitiestest] = useState(null);
//   const [torchSupported, setTorchSupported] = useState(false);
//   const [torchOn, setTorchOn] = useState(false);
//   const html5QrCodeRef = useRef(null);
//   const mountedRef = useRef(false);
//   const videoTrackRef = useRef(null); // ✅
//   const imageCaptureRef = useRef(null);

//   useEffect(() => {
//     mountedRef.current = true;
//     if (!openManual) {
//       setTimeout(() => initializeScanner(), 150);
//     }
//     return () => {
//       mountedRef.current = false;
//       stopScanner();
//     };
//   }, []);

//   const initializeScanner = async () => {
//     try {
//       const container = document.getElementById(elementId);
//       if (!container) {
//         setError(`Element with ID "${elementId}" not found in DOM.`);
//         return;
//       }

//       const devices = await Html5Qrcode.getCameras();
//       setAvailableCameras(devices);

//       if (!devices.length) {
//         setError("No cameras found.");
//         return;
//       }

//       const backCam = devices.find((d) => /back|environment/i.test(d.label));
//       const selected = backCam || devices[0];
//       setCameraId(selected.id);

//       if (!openManual) {
//         await startScanner(selected.id);
//       }
//     } catch (err) {
//       // console.error('Init error:', err);
//       setError("Failed to initialize scanner: " + (err?.message || err));
//     }
//   };

//   const startScanner = async (selectedCameraId = cameraId) => {
//     try {
//       const element = document.getElementById(elementId);
//       if (!element || !document.body.contains(element)) {
//         throw new Error(`Element with ID "${elementId}" not found in DOM.`);
//       }

//       if (html5QrCodeRef.current) {
//         await stopScanner();
//       }

//       const html5QrCode = new Html5Qrcode(elementId);
//       html5QrCodeRef.current = html5QrCode;

//       // Start scanner temporarily with 30fps
//       await html5QrCode.start(
//         { deviceId: { exact: selectedCameraId } },
//         {
//           fps: 30, // Temporary; will be updated below if needed
//           qrbox: 250,
//           experimentalFeatures: {
//             useBarCodeDetectorIfSupported: true,
//           },
//         },
//         async (decodedText) => {
//           if (onScan) onScan(decodedText);
//           await stopScanner();
//         }
//       );
//       // setTimeout(async() => {
//       //   if (html5QrCodeRef.current) {
//       //    await stopScanner();
//       //   }
//       // }, 10000);
//       setIsScanning(true);

//       // Delay to allow video stream to initialize
//       setTimeout(async () => {
//         const videoElement = document.querySelector(`#${elementId} video`);
//         const track = videoElement?.srcObject?.getVideoTracks?.()[0];
//         // videoTrackRef.current = track;
//         videoElement.srcObject = new MediaStream([track]);
//         if (!track) return;

//         const capabilities = track.getCapabilities?.();
//         setcapabilitiestest(JSON.stringify(capabilities, null, 2));

//         // ⚡ Restart scanner with max fps if available
//         const maxFps = capabilities?.frameRate?.max || 30;
//         if (maxFps !== 30) {
//           await stopScanner();
//           await html5QrCode.start(
//             { deviceId: { exact: selectedCameraId } },
//             {
//               fps: maxFps,
//               qrbox: 250,
//               experimentalFeatures: {
//                 useBarCodeDetectorIfSupported: true,
//               },
//             },
//             async (decodedText) => {
//               if (onScan) onScan(decodedText);
//               await stopScanner();
//             }
//           );
//         }

//         // ✅ Autofocus
//         if (capabilities?.focusMode?.includes('continuous')) {
//           await track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] });
//           setFocus('continuous');
//         } else if (capabilities?.focusMode?.includes('single-shot')) {
//           await track.applyConstraints({ advanced: [{ focusMode: 'single-shot' }] });
//           setFocus('single-shot');
//         } else if (capabilities?.focusMode?.includes('manual')) {
//           setFocus('manual');
//         } else {
//           setFocus('none');
//         }

//         // 🔍 Zoom
//         if (capabilities?.zoom) {
//           setZoomSupported(true);
//           setZoomRange({
//             min: capabilities.zoom.min,
//             max: capabilities.zoom.max,
//             step: capabilities.zoom.step || 0.1,
//           });
//           setZoomValue(capabilities.zoom.min);

//           if (capabilities?.torch) {
//             setTorchSupported(true);
//             imageCaptureRef.current = new window.ImageCapture(track);
//           }

//         }
//       }, 500);
//     } catch (err) {
//       console.error("Failed to start scanner:", err);
//       // setError("Failed to start scanner: " + (err?.message || err));
//     }
//   };
//   const handleCameraSwitch = async (newCameraId) => {
//     setCameraId(newCameraId);
//     await stopScanner();
//     setTimeout(() => startScanner(newCameraId), 200);
//   };
//   const stopScanner = async () => {
//     try {
//       const scanner = html5QrCodeRef.current;
//       if (!scanner) return;

//       const state = scanner.getState?.();
//       if (
//         state === Html5QrcodeScannerState.SCANNING ||
//         state === Html5QrcodeScannerState.PAUSED
//       ) {
//         await scanner.stop();
//       }

//       if (document.getElementById(elementId)) {
//         await scanner.clear();
//       }

//       html5QrCodeRef.current = null;
//       setIsScanning(false);
//       const videoElement = document.querySelector(`#${elementId} video`);
//     if (videoElement && videoElement.srcObject) {
//       const tracks = videoElement.srcObject.getTracks();
//       tracks.forEach((track) => track.stop());
//     }
//     } catch (err) {
//       // console.error("Failed to stop scanner:", err);
//     }
//   };

//   const handleZoomChange = async (value) => {
//     setZoomValue(value);
//     try {
//       const track = videoTrackRef.current;
//       if (track) {
//         await track.applyConstraints({
//           advanced: [{ zoom: parseFloat(value) }]
//         });
//       }
//     } catch (err) {
//       // console.error("Zoom error:", err);
//     }
//   };

//   const toggleTorch = async () => {
//     const track = videoTrackRef.current;
//     if (!track) return;

//     try {
//       await track.applyConstraints({
//         advanced: [{ torch: !torchOn }],
//       });
//       setTorchOn((prev) => !prev);
//     } catch (err) {
//       console.warn("Torch toggle failed:", err);
//     }
//   };

//   if (openManual) return null;

//   return (
//     <div>

//       {availableCameras.length > 1 && (
//         <div style={{ marginBottom: '10px' }}>
//           <select
//             value={cameraId}
//             // onChange={async (e) => {
//             //   const id = e.target.value;
//             //   setCameraId(id);
//             //   await stopScanner();
//             //   setTimeout(() => startScanner(id), 200);
//             // }}
//             onChange={async (e) => {
//               const newCameraId = e.target.value;
//               await handleCameraSwitch(newCameraId); // Switch camera
//             }}

//           >
//             {availableCameras.map((cam) => (
//               <option key={cam.id} value={cam.id}>
//                 {cam.label || cam.id}
//               </option>
//             ))}
//           </select>
//         </div>
//       )}

//       {/* Start / Stop Buttons */}
//       <div style={{ marginBottom: '10px' }}>
//         {!isScanning ? (
//           <button onClick={() => startScanner(cameraId)}>Start Scanner</button>
//         ) : (
//           <button onClick={stopScanner}>Stop Scanner</button>
//         )}
//       </div>

//       <div style={{ marginTop: 10 }}>
//         <p>Focus Mode: {focus}</p>
//         {/* {capabilitiestest && (
//           <>
//             <h4>Camera Capabilities:</h4>
//             <pre style={{ fontSize: 12, background: "#eee", padding: "10px" }}>
//               {capabilitiestest}
//             </pre>

//           </>
//         )} */}
//       </div>

//       <div
//         id={elementId}
//         style={{
//           position: 'relative',
//           width: '100%',
//           maxWidth: '500px',
//           height: '400px',
//           border: '1px solid #ccc',
//           marginTop: '10px',
//           overflow: 'hidden',
//           backgroundColor: '#000',
//         }}
//       >
//         {/* 🔦 Torch Button - Top Right */}
//         {torchSupported && isScanning && (
//           <button
//             onClick={toggleTorch}
//             style={{
//               position: 'absolute',
//               top: '10px',
//               right: '10px',
//               padding: '6px 10px',
//               backgroundColor: torchOn ? '#ffc107' : '#333',
//               color: '#fff',
//               border: 'none',
//               borderRadius: '5px',
//               cursor: 'pointer',
//               fontSize: '14px',
//               zIndex: 10,
//             }}
//           >
//             {torchOn ? 'Flash Off' : 'Flash On'}
//           </button>
//         )}

//         {/* 🔍 Zoom Slider - Bottom Left */}
//         {zoomSupported && (
//           <div
//             style={{
//               position: 'absolute',
//               bottom: '10px',
//               left: '10px',
//               background: 'rgba(0, 0, 0, 0.6)',
//               padding: '6px 10px',
//               borderRadius: '8px',
//               width: '100%',
//               color: '#fff',
//               zIndex: 10,
//             }}
//           >
//             <label style={{ fontSize: '12px' }}>Zoom</label>
//             <input
//               type="range"
//               min={zoomRange.min}
//               max={zoomRange.max}
//               step={zoomRange.step}
//               value={zoomValue}
//               onChange={(e) => handleZoomChange(e.target.value)}
//               style={{
//                 width: '140px',
//                 marginLeft: '10px',
//                 verticalAlign: 'middle',
//               }}
//             />
//             <span style={{ marginLeft: '8px' }}>{zoomValue}x</span>
//           </div>
//         )}
//       </div>
//       {error && <p style={{ color: 'red' }}>{error}</p>}
//     </div>
//   );
// };

{
  /* <div
  style={{
    position: 'relative', // Make this container relative for absolute positioning inside
    width: '100%',
    maxWidth: '500px',
    height: '400px',
    border: '1px solid #ccc',
    marginTop: '10px',
  }}
>
{torchSupported && isScanning && (
    <button
      onClick={toggleTorch}
      style={{
        position: 'absolute',
        bottom: '10px',
        right: '10px',
        padding: '8px 12px',
        backgroundColor: torchOn ? '#ffc107' : '#222',
        color: '#fff',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        zIndex: 2,
      }}
    >
      {torchOn ? 'Flash Off' : 'Flash On'}
    </button>
  )}
  {/* Scanner Video 
  <div
    id={elementId}
    style={{
      width: '100%',
      height: '100%',
    }}
  ></div>
 {torchSupported && isScanning && (
    <button
      onClick={toggleTorch}
      style={{
        position: 'absolute',
        bottom: '10px',
        right: '10px',
        padding: '8px 12px',
        backgroundColor: torchOn ? '#ffc107' : '#222',
        color: '#fff',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        zIndex: 2,
      }}
    >
      {torchOn ? 'Flash Off' : 'Flash On'}
    </button>
  )}
  {/* Zoom Slider Overlay 
  {zoomSupported && (
    <div
      style={{
        position: 'absolute',
        bottom: '10px',
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'rgba(0, 0, 0, 0.5)',
        padding: '8px 12px',
        borderRadius: '8px',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        minWidth: '250px',
        justifyContent: 'space-between',
      }}
    >
      <label style={{ marginRight: '10px' }}>Zoom:</label>
      <input
        type="range"
        min={zoomRange.min}
        max={zoomRange.max}
        step={zoomRange.step}
        value={zoomValue}
        onChange={(e) => handleZoomChange(e.target.value)}
        style={{ flex: 1, marginRight: '10px', cursor: 'pointer' }}
      />
      <span  style={{
        width: '40px', 
        fontFamily: 'monospace',
        textAlign: 'right',
        display: 'inline-block',
      }}>{zoomValue}x</span>
    </div>
  )}
</div> */
}

// import React, { useEffect, useRef, useState } from 'react';
// import { Html5QrcodeScanner, Html5Qrcode } from 'html5-qrcode';

// const QRScanner = ({ elementId, openManual, onScan }) => {
//   const [cameraId, setCameraId] = useState(null);
//   const scannerRef = useRef(null); // Ref to hold the scanner instance
//   const [error, setError] = useState(null);
//   const [hasLoggedError, setHasLoggedError] = useState(false);

//   useEffect(() => {
//     let isMounted = true; // To avoid updating state after unmount
//     let scanner; // Declare scanner variable to store instance

//     if (openManual) return; // Do not initialize the scanner if in manual mode

//     // Function to initialize the scanner
//     const initializeScanner = () => {
//       // Only initialize the scanner if it is not already initialized
//       if (scannerRef.current) return; // Avoid re-initializing scanner if it's already set

//       scanner = new Html5QrcodeScanner(elementId, {
//         fps: 60, // Frames per second (higher = smoother scanning)
//         qrbox: 400, // Size of the scanning box
//       });

//       // Render the scanner and provide success and error callbacks
//       scanner.render(
//         (decodedText) => {
//           if (onScan) {
//             onScan(decodedText); // Call the onScan callback with the decoded text
//           }
//           if (isMounted) {
//             // Stop the scanner after a successful scan
//             scanner.clear(); // Clear the scanner
//             if (onScan) {
//               onScan(decodedText); // Call the onScan callback with the decoded text
//             }
//           }
//         },
//         // (errorMessage) => {
//         //   // Log errors only once, prevent logging NotFoundException repeatedly
//         //   if (errorMessage.includes("NotFoundException")) {
//         //     if (!hasLoggedError) {
//         //       setHasLoggedError(true); // Log once and prevent logging continuously
//         //     }
//         //     scanner.resume(); // Resume scanning without clearing the camera feed
//         //   } else {
//         //     console.error("Error scanning QR code: ", errorMessage); // Log actual errors
//         //     if (isMounted) {
//         //       setError(errorMessage); // Set the error message to be displayed
//         //       scanner.clear(); // Clear the scanner after an error
//         //     }
//         //   }
//         // }
//       );

//       // Store the scanner instance in the ref for cleanup later
//       scannerRef.current = scanner;
//     };

//     // Fetch the available cameras and initialize the scanner
//     Html5Qrcode.getCameras()
//       .then((devices) => {
//         if (devices && devices.length > 0) {
//           initializeScanner(); // Initialize the scanner if cameras are available
//         } else {
//           console.error("No cameras found");
//           if (isMounted) {
//             setError("No cameras found."); // If no camera is found, show an error
//           }
//         }
//       })
//       .catch((err) => {
//         console.error("Error fetching cameras: ", err); // Log any errors in fetching cameras
//         if (isMounted) {
//           setError("Error fetching cameras."); // Set error state if cameras cannot be fetched
//         }
//       });

//     // Cleanup function to stop the scanner when the component unmounts
//     return () => {
//       isMounted = false; // Avoid setting state after unmount
//       if (scannerRef.current) {
//         scannerRef.current.clear(); // Clear the scanner on unmount
//         scannerRef.current = null; // Nullify the scanner reference to prevent reuse
//       }
//     };
//   }, []); // Dependency array ensures the effect only runs when necessary

//   return (
//     <div>
//       {!openManual && (
//         // This div is where the camera feed will be rendered by Html5QrcodeScanner
//         <div
//           id={elementId} // The scanner will render the video feed inside this div
//           style={{ width: "100%", height: "400px", border: "1px solid black" }}
//         ></div>
//       )}
//       {error && <p style={{ color: "red" }}>{error}</p>}
//     </div>
//   );
// };

// export default QRScanner;
